import { formatFixedTime, parseRecipients } from "../../../utils/function";
import { TemplateModule } from "../../marketing-pages/templates";
import {
  ActionNode,
  BotJourney,
  buildSingleCondition,
  DEFAULT_TRIGGER_INTERVAL,
  KeywordEntry,
  makeKwId,
  RcsFlowDefinition,
  RcsFlowEdge,
  RcsFlowNode,
  TriggerNode,
  WAIT_TIMEOUT_UNIT_MULTIPLIERS,
} from "./types";

export const TRIGGERS = [
  "On Message",
  "New Lead",
  "Run at Specific Time",
  "Lead from CTWA",
  "On Event",
  "RCS Trigger Node",
];

// Shown in the Actions tab only while the RCS trigger is on canvas — RCS
// bots run on a separate backend (see actionNodesToRcsFlow/rcsFlowToActionNodes
// below) with its own action set, distinct from the WhatsApp journey actions.
export const RCS_ACTIONS = [
  "Send RCS Template",
  "Send Menu",
  "Send Carousel",
  "Send Text",
  "Send Email",
  "Call API",
  "Wait for Reply",
  "Branch",
  "Set Variable",
  "Escalate to Human",
  "Confirm Order",
];

export const ACTIONS = [
  "Send Interactive Message",
  "Send Message Template",
  "WhatsApp Message",
  "Send Email",
  "Add or Remove Tags",
  "Run at Specific Time",
  "Wait Until",
  "Wait Till",
  "Assign Agent",
  "Add to Broadcast",
  "Store Data",
  "Create Support Ticket",
  "Send Internal Notification",
  "Condition",
  "Call API",
  "Set Delay Time",
  "Mark as Sent",
  "Set Customer Attribute",
  "Send Birthday Message",
  "Fetch Current Date",
  "Fetch Audience",
];

// Curated per-trigger action shortlist (Eben 2026-07-30: "the action list is
// getting too much... action should come by the trigger selected") — keyed
// by the exact TRIGGERS string. Falls back to the full ACTIONS list above for
// any trigger not listed here (and for RCS Trigger Node, see RCS_ACTIONS).
// Not a hard restriction on the data model — every action here already
// exists in ACTIONS/ActionNode, this only narrows what the picker shows by
// default per trigger.
export const ACTIONS_BY_TRIGGER: Record<string, string[]> = {
  "New Lead": ["Send Email", "Condition", "Send Message", "Create Support Ticket"],
  "On Event": [
    "Wait Until",
    "Send Message",
    "Assign Agent",
    "Set Delay Time",
    "Store Data",
    "Send Internal Notification",
    "Create Support Ticket",
  ],
  "Run at Specific Time": [
    "Wait Until",
    "Wait Till",
    "Send Message Template",
    "WhatsApp Message",
    "Condition",
    "Fetch Audience",
    "Send Birthday Message",
    "Create Support Ticket",
  ],
  "On Message": ["Send Message", "Send Interactive Message", "Fetch Audience", "Condition", "Create Support Ticket"],
  "Lead from CTWA": ["Send Email", "Condition", "Send Message", "Create Support Ticket", "Fetch Audience"],
};


export const getDefaultConfigForActionType = (actionType: string): Record<string, any> => {
  const configs: Record<string, Record<string, any>> = {
    "Wait Until": {
      waitMode: "date",
      dateAttribute: "Appointment_Date",
      offsetValue: 1,
      offsetUnit: "Days",
      offsetDirection: "Before",
    },
    "Fetch Audience": {
      dataSource: "csv",
      dateColumn: "Appointment_Date",
      dateFormat: "dd-mm-yyyy",
      templateName: "",
      language: "en_US",
    },
    "Send Message": {
      message: "",
      media_url: null,
      media_type: null,
    },
    "Assign Agent": {
      agentId: "",
      agentName: "",
      notes: "",
    },
    "Send Email": {
      subject: "",
      body: "",
      to: "",
      templateName: "",
    },
    "Condition": {
      customerAttribute: "user_response",
      condition: "equals",
      value: "",
      true_next: "",
      false_next: "",
    },
    "WhatsApp Message": {
      channel: "WhatsApp",
      templateId: "",
      templateName: "",
      language: "en_US",
      variables: {},
    },
    "Set Delay Time": {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
    "Store Data": {
      dataToStore: "",
      storeIn: "contact_profile",
    },
    "Create Support Ticket": {
      priority: "high",
      notes: "",
    },
    "Send RCS Template": {
      template_id: "",
      template_variables: {},
    },
    "Send Menu": {
      replyText: "",
      chips: [],
    },
    "Send Carousel": {
      replyText: "",
      cards: [],
    },
    "Send Text": {
      replyText: "",
    },
    "Wait for Reply": {
      true_next: "",
      false_next: "",
    },
    "Set Variable": {
      variable: "",
      value: "",
    },
    "Escalate to Human": {
      transfer_reason: "",
    },
    "Confirm Order": {
      replyText: "",
    },
    "Branch": {
      variable: "",
      equals: "",
      true_next: "",
      false_next: "",
    },
    "Send Internal Notification": {
      channel: "WhatsApp",
      team: "support",
      templateName: "",
      message: "",
    },
  };

  return configs[actionType] || {};
};

export const makeActionNode = (
  type: string, // Change from ActionNode["type"] to string for more flexibility
  config: Record<string, any>,
  index: number
): ActionNode => ({
  id: `preset_${index + 1}_${type.toLowerCase().replace(/\s+/g, "_")}`,
  type: type as ActionNode["type"], // Cast to ActionNode type
  config,
  x: 200,
  y: 200,
});

export const SCORE_OPERATORS = new Set([
  "equals",
  "not_equals",
  "greater_than",
  "greater_than_or_equal",
  "less_than",
  "less_than_or_equal",
]);

export const EVENT_CONDITION_ATTRIBUTES = new Set([
  "email_engaged",
  "email_event_type",
  "reply_received",
  "reply_text",
  "call_engaged",
  "call_outcome",
]);


export function hydrateJourneyToCanvas(journey: BotJourney) {
  const topLevelJourney = journey as any;
  const version = journey.versions?.[0];
  const def = (topLevelJourney.definition ?? version?.definition) as any;
  if (!def) return { triggerNode: null, actionNodes: [] };

  const rawTrigger = topLevelJourney.trigger ?? def.trigger;
  const triggerType: string =
    typeof rawTrigger === "string" ? rawTrigger : rawTrigger?.type ?? "";
  const normalizedTriggerType = triggerType.toLowerCase().replace(/\s+/g, "_");
  const triggerConfig = topLevelJourney.trigger_config ?? def.trigger_config ?? rawTrigger?.config ?? {};
  const steps = def.steps ?? topLevelJourney.steps ?? [];

  // ── Trigger ──
  let mappedTrigger: TriggerNode | null = null;

  if (normalizedTriggerType === "on_message") {
    let keywords: KeywordEntry[] = [];
    let restart_journey = false;

    if ((triggerConfig.keywords ?? []).length || "restart_journey" in triggerConfig) {
      // New flat shape
      keywords = (triggerConfig.keywords ?? []).map((kw: any, i: number) => ({
        id: kw.id ?? makeKwId(i),
        text: kw.text ?? kw.value ?? "",
        match_similar: kw.match_similar ?? false,
      }));
      restart_journey = triggerConfig.restart_journey ?? false;
    } else if (def.trigger?.conditions) {
      // Old nested shape fallback
      const first = def.trigger.conditions.find((c: any) => c.field === "message_body");
      keywords = [{ id: "kw_1", text: first?.value ?? "", match_similar: false }];
    }

    mappedTrigger = {
      type: "On Message",
      keywords: keywords.length ? keywords : [{ id: "kw_1", text: "", match_similar: false }],
      restart_journey,
    };
  }

  if (normalizedTriggerType === "scheduled") {
    let fixedTime = triggerConfig?.fixed_time ?? triggerConfig?.fixedTime ?? "";
    if (fixedTime && fixedTime.includes(" ")) {
      // Example: "06-11-2026 02:30:00PM" → convert to "2026-06-11T14:30"
      const [datePart, timePartWithPeriod] = fixedTime.split(" ");
      const [month, day, year] = datePart.split("-");
      let [time, period] = timePartWithPeriod.split(/(AM|PM)/i);

      let [hour, minute] = time.split(":").map(Number);
      if (period?.toUpperCase() === "PM" && hour !== 12) hour += 12;
      if (period?.toUpperCase() === "AM" && hour === 12) hour = 0;

      fixedTime = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    }

    mappedTrigger = {
      type: "Run at Specific Time",
      fixedTime: fixedTime,
      triggerInterval: triggerConfig?.trigger_interval ?? triggerConfig?.triggerInterval ?? DEFAULT_TRIGGER_INTERVAL,
      recipients: (triggerConfig?.recipients ?? []).join("\n"),
    };
  }

  if (["new_lead", "lead_created", "lead_submitted"].includes(normalizedTriggerType)) {
    mappedTrigger = {
      type: "New Lead",
      source: triggerConfig?.source ?? "Website Form",
    };
  }

  if (["on_event", "event", "external_event"].includes(normalizedTriggerType)) {
    const savedEventTypes = Array.isArray(triggerConfig?.event_types)
      ? triggerConfig.event_types.filter((v: any) => typeof v === "string" && v)
      : [];
    const legacySingle = triggerConfig?.event_type ?? triggerConfig?.eventType ?? "";
    const eventTypesFinal = savedEventTypes.length
      ? savedEventTypes
      : legacySingle ? [legacySingle] : [];
    // Restore the operator's saved test leads so re-opening a published
    // journey shows the phone/name/date/time they typed (Joseph 06-30:
    // "the name the number does not save when i publh the bot leave the
    // page and go back i dont the details inserted").
    const savedLeads = Array.isArray(triggerConfig?.test_leads)
      ? triggerConfig.test_leads
      : [];
    mappedTrigger = {
      type: "On Event",
      eventType: eventTypesFinal[0] ?? "Appointment_Scheduled",
      eventTypes: eventTypesFinal,
      restart_journey: triggerConfig?.restart_journey ?? true,
      testLeads: savedLeads,
    } as TriggerNode;
  }

  // ── Steps → ActionNodes ──
  // Pre-collect every step id so condition true_next/false_next pointers that
  // reference a non-existent step (legacy data — serializer used to fabricate
  // step_${idx+3} even when no third sibling existed) can be cleared to "".
  // Without this, handleDropAction sees the bogus pointer as "wired" and
  // refuses to drop the user's cold-nudge action into the False branch.
  const stepIdSet = new Set<string>((steps || []).map((s: any) => s.id).filter(Boolean));
  const sanitizeBranchId = (id: string | undefined | null): string =>
    id && stepIdSet.has(id) ? id : "";

  const mappedActions: ActionNode[] = (steps || []).map((step: any, index: number) => {
    const base = {
      id: step.id || Math.random().toString(36).slice(2),
      x: 200,
      y: 150 + index * 120,
    };

    switch (step.type) {
      case "assign_agent":
        return {
          ...base,
          type: "Assign Agent" as const,
          config: {
            agentId: step.config?.agent_id ?? "",
            agentName: step.config?.agent_name ?? "",
            notes: step.config?.notes ?? "",
          },
        };

      case "add_tags":
      case "add_tag":
        return {
          ...base,
          type: "Add or Remove Tags" as const,
          config: { action: "Add Tags", tags: step.config?.tags ?? [] },
        };

      case "send_message":
        return {
          ...base,
          type: "Send Message" as const,
          config: {
            message: step.config?.body ?? (step as any).value ?? "",
            media_url: step.config?.media_url ?? null,
            media_type: step.config?.media_type ?? null,
          },
        };

      case "send_template": {
        // Hydrate as "Send Message Template" card so the template picker
        // repopulates the previously chosen template. The saved name lets
        // the card resolve the matching template object (and its header
        // media + variables) from the loaded templates list.
        const templateName = step.config?.template_name ?? step.config?.template ?? "";
        const cfg = step.config ?? {};
        return {
          ...base,
          type: "Send Message Template" as const,
          config: {
            templateId: cfg.templateId ?? cfg.template_id ?? "",
            templateName,
            template: templateName,
            language: cfg.language ?? "en_US",
            channel: cfg.channel ?? "WhatsApp",
            waba_id: cfg.waba_id ?? "",
            category: cfg.category ?? "",
            wa_template_id: cfg.wa_template_id ?? "",
            message: cfg.message ?? "",
            variables: cfg.variables ?? {},
            components: cfg.components ?? [],
            sendTime: cfg.send_time ?? "now",
            media_url: cfg.media_url ?? null,
            media_type: cfg.media_type ?? null,
          },
        };
      }

      case "whatsapp_message": {
        // Hydrate the dedicated "WhatsApp Message" card with everything
        // needed to repopulate the template picker + variable mapping.
        const templateName = step.config?.template_name ?? "";
        const cfg = step.config ?? {};
        return {
          ...base,
          type: "WhatsApp Message" as const,
          config: {
            templateId: cfg.templateId ?? cfg.template_id ?? "",
            templateName,
            template: templateName,
            language: cfg.language ?? "en_US",
            channel: cfg.channel ?? "WhatsApp",
            waba_id: cfg.waba_id ?? "",
            message: cfg.body ?? cfg.message ?? "",
            variables: cfg.variables ?? {},
            media_url: cfg.media_url ?? null,
            media_type: cfg.media_type ?? null,
          },
        };
      }

      case "close_conversation":
        return { ...base, type: "Close Conversation" as const, config: {} };

      case "call_api":
        return {
          ...base,
          type: "Call API" as const,
          config: { url: step.config?.url ?? "", method: step.config?.method ?? "POST" },
        };

      case "send_email":
        return {
          ...base,
          type: "Send Email" as const,
          config: {
            subject: step.config?.subject ?? "",
            body: step.config?.body ?? step.config?.html ?? "",
            to: step.config?.to ?? step.config?.recipient_field ?? "email",
          },
        };

      case "condition": {
        const firstCondition = step.config?.conditions?.[0] ?? {};
        const waitTimeoutSeconds = step.config?.wait_timeout_seconds;
        // Pick the LARGEST unit that divides waitTimeoutSeconds evenly, so
        // 120s loads as "2 Minutes" (not "1 Hour" rounded from 0.033). Without
        // this, ALL saved seconds get displayed as "1 Hour" regardless of value.
        const inferTimeoutUnitAndValue = (secs?: number) => {
          if (!secs || secs <= 0) return { value: undefined, unit: undefined };
          if (secs % 86400 === 0) return { value: secs / 86400, unit: "days" };
          if (secs % 3600 === 0) return { value: secs / 3600, unit: "hours" };
          if (secs % 60 === 0) return { value: secs / 60, unit: "minutes" };
          return { value: secs, unit: "seconds" };
        };
        const { value: vtValue, unit: vtUnit } = inferTimeoutUnitAndValue(waitTimeoutSeconds);
        return {
          ...base,
          type: "Condition" as const,
          config: {
            conditions: step.config?.conditions ?? [],
            logic: step.config?.logic ?? "AND",
            customerAttribute: firstCondition.variable ?? "",
            condition: firstCondition.operator ?? "",
            value: firstCondition.value ?? "",
            operator: firstCondition.operator ?? "",
            wait_timeout_seconds: waitTimeoutSeconds,
            waitTimeoutValue: vtValue,
            waitTimeoutUnit: vtUnit,
            // WhatsApp-condition fields — restore what was published so the
            // side-panel reopens on the WhatsApp state instead of resetting
            // to the Email default (Joseph 2026-08-04: "after i published
            // …instead of the saved that has been published"). Match the
            // fields the serializer below writes.
            channel: step.config?.channel,
            customerAttributes: step.config?.customerAttributes,
            whatsapp_actions: step.config?.whatsapp_actions,
            match_mode: step.config?.match_mode,
            // Clear branch pointers that don't actually reference an existing
            // step — legacy data has bogus "step_4" pointers from when the
            // serializer fabricated step ids. Without this, the user can't
            // drop new actions into the "empty" False branch on edit.
            true_next: sanitizeBranchId(step.config?.true_next),
            false_next: sanitizeBranchId(step.config?.false_next),
          },
        };
      }

      case "run_at_specific_time":
      case "scheduled":
        return {
          ...base,
          type: "Run at Specific Time" as const,
          config: {
            fixedTime: step.config?.fixed_time ?? "",
            triggerInterval: step.config?.trigger_interval ?? DEFAULT_TRIGGER_INTERVAL,
            recipients: (step.config?.recipients ?? []).join("\n"),
          },
        };

      case "wait_until":
        return {
          ...base,
          type: "Wait Until" as const,
          config: {
            waitMode: "date",
            dateAttribute: step.config?.date_attribute ?? step.config?.dateAttribute ?? "Appointment_Date",
            offsetValue: step.config?.offset_value ?? step.config?.offsetValue ?? 1,
            offsetUnit: step.config?.offset_unit ?? step.config?.offsetUnit ?? "Days",
            offsetDirection: step.config?.offset_direction ?? step.config?.offsetDirection ?? "Before",
          },
        };

      case "wait_till": {
        // Backend uses one step_type ("wait_till") for two UI cards:
        //   mode: "date_attribute" → Wait Until card (event-driven date wait)
        //   default                 → Wait Till card (signal/duration wait)
        if (step.config?.mode === "date_attribute") {
          return {
            ...base,
            type: "Wait Until" as const,
            config: {
              waitMode: "date",
              dateAttribute:
                step.config?.date_attribute ?? step.config?.dateAttribute ?? "Appointment_Date",
              offsetValue:
                step.config?.offset_value ?? step.config?.offsetValue ?? 1,
              offsetUnit:
                // capitalize for the UI dropdown ("Days" not "days")
                ((step.config?.offset_unit ?? step.config?.offsetUnit ?? "Days") as string)
                  .replace(/^./, (c: string) => c.toUpperCase()),
              offsetDirection:
                ((step.config?.offset_direction ?? step.config?.offsetDirection ?? "Before") as string)
                  .replace(/^./, (c: string) => c.toUpperCase()),
            },
          };
        }

        // Parse duration back from "2d3h45m" → days/hours/minutes inputs
        const duration: string = step.config?.duration ?? "2d";
        const dMatch = /(\d+)d/.exec(duration);
        const hMatch = /(\d+)h/.exec(duration);
        const mMatch = /(\d+)m/.exec(duration);
        return {
          ...base,
          type: "Wait Till" as const,
          config: {
            days: dMatch ? Number(dMatch[1]) : 0,
            hours: hMatch ? Number(hMatch[1]) : 0,
            minutes: mMatch ? Number(mMatch[1]) : 0,
            waitForEvent: step.config?.wait_for_event ?? "email_engagement_received",
            timeoutContinue: step.config?.timeout_continue ?? true,
          },
        };
      }

      case "store_data":
      case "Store Data":
        return {
          ...base,
          type: "Store Data" as const,
          config: {
            dataToStore: step.config?.dataToStore ?? step.config?.data_to_store ?? "",
            storeIn: step.config?.storeIn ?? step.config?.store_in ?? "contact_profile",
          },
        };

      case "create_support_ticket":
      case "Create Support Ticket":
        return {
          ...base,
          type: "Create Support Ticket" as const,
          config: {
            priority: step.config?.priority ?? "high",
            notes: step.config?.notes ?? "",
          },
        };

      case "send_internal_notification":
      case "Send Internal Notification":
        return {
          ...base,
          type: "Send Internal Notification" as const,
          config: {
            channel: step.config?.channel ?? "WhatsApp",
            team: step.config?.team ?? "support",
            templateName: step.config?.template_name ?? step.config?.templateName ?? "",
            message: step.config?.message ?? "",
          },
        };

      default:
        return {
          ...base,
          type: step.type
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c: string) => c.toUpperCase()) as ActionNode["type"],
          config: step.config ?? {},
        };
    }
  });

  return { triggerNode: mappedTrigger, actionNodes: mappedActions };
}



export function buildJourneyPayload(
  name: string,
  triggerNode: TriggerNode | null,
  actionNodes: ActionNode[],
  templates: TemplateModule[],
  meta: { channel?: string; account?: string; created_by?: string } = {}
) {
  const rawKeywords: KeywordEntry[] = triggerNode?.keywords ?? [];
  const keywords = rawKeywords.map((kw, i) => ({
    id: kw.id || `kw_${i + 1}`,
    text: kw.text,
    match_similar: kw.match_similar,
  }));

  const isScheduledTrigger = triggerNode?.type === "Run at Specific Time";
  const isNewLeadTrigger = triggerNode?.type === "New Lead";
  const isEventTrigger = triggerNode?.type === "On Event";

  const trigger_config = isScheduledTrigger
    ? {
      fixed_time: formatFixedTime(triggerNode?.fixedTime as string | undefined),
      trigger_interval: triggerNode?.triggerInterval ?? DEFAULT_TRIGGER_INTERVAL,
      recipients: parseRecipients(triggerNode?.recipients as string | string[] | undefined),
    }
    : isNewLeadTrigger
      ? {
        source: triggerNode?.source ?? "Website Form",
      }
      : isEventTrigger
        ? {
          // Multi-event support (Joseph 06-30 screenshot shows the trigger
          // bound to two events: On_Ticket_Closed + Feedback_Collection_Start).
          // Persist both the array AND the legacy single value so backward-
          // compatible code keeps working.
          event_types: Array.isArray((triggerNode as any)?.eventTypes)
            ? (triggerNode as any).eventTypes.filter((v: any) => typeof v === "string" && v)
            : [],
          event_type: triggerNode?.eventType ?? "Appointment_Scheduled",
          restart_journey: triggerNode?.restart_journey ?? true,
          // Persist the operator's saved leads (Joseph 06-30: "the name
          // the number does not save when i publh the bot leave the
          // page and go back i dont the details inserted"). Round-trips
          // via trigger_config.test_leads; hydrateJourneyToCanvas reads
          // them back onto trigger.testLeads on mount.
          test_leads: Array.isArray((triggerNode as any)?.testLeads)
            ? (triggerNode as any).testLeads
            : [],
        }
        : {
          keywords,
          restart_journey: triggerNode?.restart_journey ?? false,
        };

  // Translation map from client-side node IDs to the step_${idx+1} ids used
  // when serializing. Conditions reference siblings via true_next/false_next
  // — those must be REWRITTEN to the new step ids, otherwise the pointers
  // dangle once the journey is saved + reloaded (which collapses the False
  // branch back to "Drop action for False path" on every edit).
  const nodeIdToStepId = new Map<string, string>(
    actionNodes.map((n, i) => [n.id, `step_${i + 1}`])
  );
  const remap = (id: string | undefined | null): string =>
    (id && nodeIdToStepId.get(id)) || "";

  const steps = actionNodes.map((node, idx) => {
    const stepId = `step_${idx + 1}`;

    switch (node.type) {
      case "Add or Remove Tags":
        return {
          id: stepId,
          type: "add_tags",
          config: { tags: node.config?.tags ?? [] }
        };

      case "Assign Agent":
        return {
          id: stepId,
          type: "assign_agent",
          config: {
            agent_id: node.config?.agentId ?? node.config?.agent_id ?? "",
            agent_name: node.config?.agentName ?? "",
            notes: node.config?.notes ?? "",
          }
        };

      case "Send Message":
        return {
          id: stepId,
          type: "send_message",
          config: {
            body: node.config?.message ?? "",
            media_url: node.config?.media_url ?? null,
            media_type: node.config?.media_type ?? null
          }
        };

      case "Send Message Template": {
        const vars: Record<number, string> = node.config?.variables ?? {};

        // Find the selected template from the templates array
        const selectedTemplate = templates.find((t) => t.id === node.config?.templateId);
        let messageText = selectedTemplate?.content ?? "";

        // Replace variables in the message text ({{1}}, {{2}}, etc.)
        Object.entries(vars).forEach(([index, value]) => {
          messageText = messageText.replace(new RegExp(`\\{\\{${index}\\}\\}`, 'g'), value);
        });

        return {
          id: stepId,
          type: "send_template",
          config: {
            template_name: node.config?.templateName ?? node.config?.template ?? "",
            language: node.config?.language ?? "en_US",
            message: messageText,
            channel: node.config?.channel ?? "WhatsApp",
            waba_id: node.config?.waba_id ?? "",
            send_time: node.config?.sendTime ?? "now",
            components: [],

            media_url: node.config?.media_url ?? null,
            media_type: node.config?.media_type ?? null,
          }
        };
      }

      case "Add to Broadcast":
        return {
          id: stepId,
          type: "send_template",
          config: {
            template_name: node.config?.template ?? "hello_world",
            language: node.config?.language ?? "en_US",
            message: node.config?.message ?? "",
            channel: "WhatsApp",
            waba_id: "",
            send_time: "now",
            components: node.config?.components ?? []
          }
        };

      case "Close Conversation":
        return { id: stepId, type: "close_conversation", config: {} };

      case "Call API":
        return {
          id: stepId,
          type: "call_api",
          config: { url: node.config?.url ?? "", method: node.config?.method ?? "POST" }
        };

      case "Send Email":
        return {
          id: stepId,
          type: "send_email",
          config: {
            subject: node.config?.subject ?? "",
            body: node.config?.body ?? "",
            to: node.config?.to ?? "email",
            template_name: node.config?.templateName ?? node.config?.template ?? ""
          }
        };

      case "Set Delay Time": {
        const { days = 0, hours = 0, minutes = 0, seconds = 0 } = node.config;
        const delay_seconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
        return { id: stepId, type: "set_delay", config: { delay_seconds } };
      }

      case "Wait Till": {
        // Pauses workflow until backend signal arrives or timeout elapses.
        // Maps to backend wait_till step with wait_for_event + duration string.
        const days = Number(node.config?.days ?? 2);
        const hours = Number(node.config?.hours ?? 0);
        const minutes = Number(node.config?.minutes ?? 0);
        const duration = `${days}d${hours}h${minutes}m`;
        return {
          id: stepId,
          type: "wait_till",
          config: {
            wait_for_event:
              node.config?.waitForEvent ?? "email_engagement_received",
            duration,
            timeout_continue: node.config?.timeoutContinue ?? true,
          },
        };
      }

      case "Wait Until":
        // Backend's _handle_wait_till switches on step_type == "wait_till".
        // We pass mode: "date_attribute" so it picks the date-based branch
        // (vs the existing signal/duration branch).
        return {
          id: stepId,
          type: "wait_till",
          config: {
            mode: "date_attribute",
            date_attribute: node.config?.dateAttribute ?? "Appointment_Date",
            offset_value: Number(node.config?.offsetValue ?? 1),
            offset_unit: (node.config?.offsetUnit ?? "Days").toLowerCase(),
            offset_direction: (node.config?.offsetDirection ?? "Before").toLowerCase(),
          },
        };

      case "Run at Specific Time":
        return {
          id: stepId,
          type: "run_at_specific_time",
          config: {
            fixed_time: formatFixedTime(node.config?.fixedTime),
            trigger_interval: node.config?.triggerInterval ?? DEFAULT_TRIGGER_INTERVAL,
            recipients: parseRecipients(node.config?.recipients),
          }
        };

      case "Fetch Current Date":
        return {
          id: stepId,
          type: "fetch_current_date",
          config: {
            date_source: node.config?.dateSource ?? "current_date",
            date_format: node.config?.dateFormat ?? "dd-mm-yyyy",
          }
        };

      case "Fetch Audience":
        return {
          id: stepId,
          type: "fetch_audience",
          config: {
            data_source: node.config?.dataSource ?? "csv",
            date_column: node.config?.dateColumn ?? "Birthday_Date",
            date_format: node.config?.dateFormat ?? "dd-mm-yyyy",
            template_name: node.config?.templateName ?? "birthday_greet",
            language: node.config?.language ?? "en_US",
            file_name: node.config?.fileName ?? "",
          }
        };

      case "Set Customer Attribute":
        return {
          id: stepId,
          type: "set_customer_attribute",
          config: {
            attribute: node.config?.attribute ?? "",
            value: node.config?.value ?? ""
          }
        };

      case "Store Data":
        return {
          id: stepId,
          type: "store_data",
          config: {
            data_to_store: node.config?.dataToStore ?? "",
            store_in: node.config?.storeIn ?? "contact_profile",
          }
        };

      case "Create Support Ticket":
        return {
          id: stepId,
          type: "create_support_ticket",
          config: {
            priority: node.config?.priority ?? "high",
            notes: node.config?.notes ?? "",
          }
        };

      case "Send Internal Notification":
        return {
          id: stepId,
          type: "send_internal_notification",
          config: {
            channel: node.config?.channel ?? "WhatsApp",
            team: node.config?.team ?? "support",
            template_name: node.config?.templateName ?? "",
            message: node.config?.message ?? "",
          }
        };

      case "Condition":
        if (node.config?.variant === "birthday") {
          return {
            id: stepId,
            type: "condition",
            config: {
              conditions: (node.config?.conditions ?? []).map((condition: any) => ({
                variable: condition.variable ?? "",
                operator: condition.operator ?? "matches_today",
                value: condition.value ?? "",
              })),
              logic: node.config?.logic ?? "AND",
              ...(() => {
                const b = getConditionBranches(node.config, `step_${idx + 2}`, "");
                return { true_next: remap(b.true_next) || b.true_next, false_next: remap(b.false_next) || b.false_next };
              })(),
            }
          };
        }



        const regularConditions = Array.isArray(node.config?.conditions) && node.config.conditions.length
          ? node.config.conditions.map(buildSingleCondition)
          : [buildSingleCondition({
            variable: node.config?.customerAttribute ?? "user_response",
            operator: node.config?.condition ?? "equals",
            value: node.config?.value ?? "",
          })];

        return {
          id: stepId,
          type: "condition",
          config: {
            conditions: regularConditions,
            logic: node.config?.logic ?? "AND",
            ...(getWaitTimeoutSeconds(node.config, regularConditions[0]?.variable)
              ? { wait_timeout_seconds: getWaitTimeoutSeconds(node.config, regularConditions[0]?.variable) }
              : {}),
            // WhatsApp-condition fields — persisted so the panel reopens
            // on the same state after publish (Joseph 2026-08-04) AND so
            // the backend's _lookup_published_wa_actions can read the
            // template + delay from the published definition. Only write
            // when set so legacy Email conditions don't grow noise keys.
            ...(node.config?.channel
              ? { channel: node.config.channel }
              : {}),
            ...(Array.isArray(node.config?.customerAttributes) && node.config.customerAttributes.length
              ? { customerAttributes: node.config.customerAttributes }
              : {}),
            ...(Array.isArray(node.config?.whatsapp_actions) && node.config.whatsapp_actions.length
              ? { whatsapp_actions: node.config.whatsapp_actions }
              : {}),
            ...(node.config?.match_mode
              ? { match_mode: node.config.match_mode }
              : {}),
            // Only fabricate fallback step ids when an actual array element
            // exists at that position. Without this guard a Condition saved
            // with only one downstream sibling would point false_next at a
            // non-existent step_${idx+3}, breaking the False branch on reload.
            // remap() translates client-side node ids (preset_3_whatsapp_message,
            // random hex from drag-add) into the step_${i+1} ids used in the
            // serialized output so the saved pointer matches the saved step.
            ...(() => {
              const b = getConditionBranches(
                node.config,
                idx + 1 < actionNodes.length ? `step_${idx + 2}` : "",
                idx + 2 < actionNodes.length ? `step_${idx + 3}` : "",
              );
              return {
                true_next: remap(b.true_next) || b.true_next,
                false_next: remap(b.false_next) || b.false_next,
              };
            })(),
          }
        };

      case "Mark as Sent":
        return {
          id: stepId,
          type: "mark_as_sent",
          config: {
            data_source: node.config?.dataSource ?? "csv",
            update_column: node.config?.updateColumn ?? "Last_wished_on",
            update_value: node.config?.updateValue ?? "Today",
          }
        };
      case "Send Birthday Message": {
        const vars: Record<number, string> = node.config?.variables ?? {};
        const selectedTemplate = templates.find((t) => t.id === node.config?.templateId);
        let messageText = selectedTemplate?.content ?? "";
        Object.entries(vars).forEach(([index, value]) => {
          messageText = messageText.replace(
            new RegExp(`\\{\\{${index}\\}\\}`, "g"),
            value
          );
        });

        return {
          id: stepId,
          type: "send_template",
          config: {
            template_name: node.config?.templateName ?? "",
            language: node.config?.language ?? "en_US",
            message: messageText,
            channel: node.config?.channel ?? "WhatsApp",
            waba_id: node.config?.waba_id ?? "",
            send_time: "now",
            components: [],
          },
        };
      }

      case "WhatsApp Message": {
        // Build final message by substituting {{N}} with mapped lead field tokens
        const vars: Record<number, string> = node.config?.variables ?? {};
        const selectedTemplate = templates.find((t) => t.id === node.config?.templateId);
        const rawBody = selectedTemplate?.content ?? node.config?.message ?? "";

        const finalMessage = rawBody.replace(/\{\{(\d+)\}\}/g, (_: string, n: string) => {
          const mapped = vars[Number(n)];
          return mapped?.trim() ? mapped : `{{${n}}}`;
        });

        return {
          id: stepId,
          type: "whatsapp_message",
          config: {
            body: finalMessage,
            language: node.config?.language ?? "en_US",
            template_name: node.config?.templateName ?? "",
            channel: node.config?.channel ?? "WhatsApp",
            waba_id: node.config?.waba_id ?? "",
            variables: vars,
            // Pass media so celery send_whatsapp builds the required Meta header
            // component for templates with image/video/document/audio headers.
            media_url: node.config?.media_url ?? null,
            media_type: node.config?.media_type ?? null,
            // Reply-dispatch wiring the backend needs:
            //   purpose="appointment_reminder"  → workflows.py routes
            //     YES/NO/Reschedule to the correct follow-up templates.
            //   wait_for_response / wait_for_response_seconds → workflow
            //     pauses after send waiting for the customer's tap
            //     (up to N seconds, default 24h).
            // These are set on the FE preset (connected-actions.tsx) but
            // the old serializer dropped them, so the dispatcher never
            // fired. (Joseph 07-01 Reschedule reply landed in convohub
            // at 09:40 but no auto-response because j275 had neither
            // field saved.)
            purpose: node.config?.purpose ?? undefined,
            wait_for_response: node.config?.wait_for_response ?? undefined,
            wait_for_response_seconds:
              node.config?.wait_for_response_seconds ?? undefined,
          },
        };
      }

      default:
        return {
          id: stepId,
          type: node.type.toLowerCase().replace(/ /g, "_"),
          config: node.config ?? {}
        };
    }
  });

  return {
    name,
    channel: "whatsapp",
    // No hardcoded `account` here. The backend resolves the tenant's WABA
    // via routes/channels.get_waba_credentials using the authenticated
    // tenant_id. Hardcoding "69dce1ebc4…" leaked a specific channel into
    // every published journey regardless of tenant — would have sent
    // every reminder from Joseph's test WABA in prod.
    ...(meta.channel && { channel: meta.channel }),
    ...(meta.account && { account: meta.account }),
    ...(meta.created_by && { created_by: meta.created_by }),
    definition: {
      trigger: isScheduledTrigger ? "scheduled" : isNewLeadTrigger ? "new_lead" : isEventTrigger ? "on_event" : "on_message",
      trigger_config,
      steps,
    },
  };
}


const getConditionBranches = (config: Record<string, any>, fallbackTrueNext = "", fallbackFalseNext = "") => ({
  true_next: config?.true_next || fallbackTrueNext,
  false_next: config?.false_next || fallbackFalseNext,
});

const getWaitTimeoutSeconds = (config: Record<string, any>, conditionVariable?: string) => {
  if (!EVENT_CONDITION_ATTRIBUTES.has(conditionVariable ?? config?.customerAttribute ?? "")) return undefined;

  // Always recompute from value × unit. Never trust a cached wait_timeout_seconds —
  // when the user changes the unit dropdown without the input value firing onChange,
  // the cached field can hold the old computation (e.g. "1 Hour" displayed but cached
  // 120s from a previous "2 Minutes" selection). Recomputing here is the source of truth.
  const timeoutValue = Number(config?.waitTimeoutValue);
  if (timeoutValue && !Number.isNaN(timeoutValue)) {
    const multiplier = WAIT_TIMEOUT_UNIT_MULTIPLIERS[config?.waitTimeoutUnit ?? "hours"] ?? WAIT_TIMEOUT_UNIT_MULTIPLIERS.hours;
    return timeoutValue * multiplier;
  }

  // Fallback: if value/unit aren't set but we have a cached seconds (e.g.
  // a hydrated-but-untouched config), use that.
  if (typeof config?.wait_timeout_seconds === "number" && config.wait_timeout_seconds > 0) {
    return config.wait_timeout_seconds;
  }
  return undefined;
};

// ─── RCS trigger — canvas ⇄ rcs_bot_service translation ──────────────────────
// The RCS trigger's actions never go through buildJourneyPayload/
// hydrateJourneyToCanvas above (those target the WhatsApp journeys backend).
// Instead they're translated to/from the separate rcs_bot_service's node/edge
// graph shape here, keeping the on-canvas model (a flat ActionNode[] array,
// same as every other trigger) identical regardless of which backend saves it.

// Nodes that fork into two branches, same mechanism "Condition" already uses:
// config.true_next/false_next point at sibling ids.
const RCS_BRANCHING_TYPES = new Set(["Wait for Reply", "Branch"]);

const RCS_ACTION_TYPE_TO_NODE_TYPE: Record<string, string> = {
  "Send RCS Template": "send_rcs_template",
  "Send Menu": "send_menu",
  "Send Carousel": "send_carousel",
  "Send Text": "send_text",
  "Send Email": "send_email",
  "Call API": "call_api",
  "Wait for Reply": "wait_for_reply",
  "Branch": "branch",
  "Set Variable": "set_variable",
  "Escalate to Human": "escalate",
  "Confirm Order": "confirm_order",
};

const NODE_TYPE_TO_RCS_ACTION_TYPE: Record<string, ActionNode["type"]> = Object.fromEntries(
  Object.entries(RCS_ACTION_TYPE_TO_NODE_TYPE).map(([action, node]) => [node, action as ActionNode["type"]]),
);

const rcsNodeConfigFromAction = (node: ActionNode): Record<string, any> => {
  switch (node.type) {
    case "Send RCS Template":
      return {
        template_id: node.config?.template_id ?? "",
        template_variables: node.config?.template_variables ?? {},
      };
    case "Send Menu":
      return { replyText: node.config?.replyText ?? "", chips: node.config?.chips ?? [] };
    case "Send Carousel":
      return { replyText: node.config?.replyText ?? "", cards: node.config?.cards ?? [] };
    case "Send Text":
      return { replyText: node.config?.replyText ?? "" };
    case "Send Email":
      return {
        to: node.config?.to ?? "",
        subject: node.config?.subject ?? "",
        html_body: node.config?.body ?? "",
        template_key: node.config?.templateName || undefined,
      };
    case "Call API":
      return {
        url: node.config?.url ?? "",
        method: node.config?.method ?? "POST",
        headers: node.config?.headers ?? {},
      };
    case "Set Variable":
      return { variables_to_set: { [node.config?.variable || "value"]: node.config?.value ?? "" } };
    case "Escalate to Human":
      return { transfer_reason: node.config?.transfer_reason ?? "" };
    case "Confirm Order":
      return { replyText: node.config?.replyText ?? "" };
    case "Branch":
      return { variable: node.config?.variable ?? "", equals: node.config?.equals ?? "" };
    case "Wait for Reply":
    default:
      return {};
  }
};

const rcsActionConfigFromNode = (node: RcsFlowNode): Record<string, any> => {
  const cfg = node.config || {};
  switch (node.type) {
    case "send_rcs_template":
      return { template_id: cfg.template_id ?? "", template_variables: cfg.template_variables ?? {} };
    case "send_menu":
      return { replyText: cfg.replyText ?? "", chips: cfg.chips ?? [] };
    case "send_carousel":
      return { replyText: cfg.replyText ?? "", cards: cfg.cards ?? [] };
    case "send_text":
      return { replyText: cfg.replyText ?? "" };
    case "send_email":
      return {
        to: cfg.to ?? "",
        subject: cfg.subject ?? "",
        body: cfg.html_body ?? "",
        templateName: cfg.template_key ?? "",
      };
    case "call_api":
      return { url: cfg.url ?? "", method: cfg.method ?? "POST", headers: cfg.headers ?? {} };
    case "set_variable": {
      const entries = Object.entries(cfg.variables_to_set || {});
      return { variable: entries[0]?.[0] ?? "", value: entries[0]?.[1] ?? "" };
    }
    case "escalate":
      return { transfer_reason: cfg.transfer_reason ?? "" };
    case "confirm_order":
      return { replyText: cfg.replyText ?? "" };
    case "branch":
      return { variable: cfg.variable ?? "", equals: cfg.equals ?? "" };
    case "wait_for_reply":
    default:
      return {};
  }
};

// Simple left-to-right layout — the unified canvas never reads positions
// back (it renders the flat array as a flex chain), but rcs_bot_service
// requires them on every stored node.
const STEP_X = 260;

export function actionNodesToRcsFlow(
  name: string,
  triggerNode: TriggerNode | null,
  actionNodes: ActionNode[],
  meta: Record<string, unknown> = {},
): RcsFlowDefinition {
  const triggerId = "trigger_1";
  const keywordEntries = triggerNode?.keywords ?? [];
  const keywords = keywordEntries.map((k) => k.text).filter((t) => t.trim());
  const matchSimilar = keywordEntries.some((k) => k.match_similar);
  const nodes: RcsFlowNode[] = [
    {
      id: triggerId,
      type: "trigger",
      label: "Trigger",
      position: { x: 0, y: 200 },
      config: {
        channels: ["rcs"],
        keywords: keywords.length ? keywords : ["hi", "hello"],
        match_mode: "exact",
        match_similar: matchSimilar,
      },
    },
  ];
  const edges: RcsFlowEdge[] = [];

  const branchTargetIds = new Set<string>();
  actionNodes.forEach((n) => {
    if (RCS_BRANCHING_TYPES.has(n.type)) {
      if (n.config?.true_next) branchTargetIds.add(n.config.true_next);
      if (n.config?.false_next) branchTargetIds.add(n.config.false_next);
    }
  });
  const topLevel = actionNodes.filter((n) => !branchTargetIds.has(n.id));

  let prevId = triggerId;
  topLevel.forEach((node, index) => {
    const nodeType = RCS_ACTION_TYPE_TO_NODE_TYPE[node.type];
    if (!nodeType) return; // skip non-RCS action types if any slipped in
    nodes.push({
      id: node.id,
      type: nodeType as RcsFlowNode["type"],
      label: node.type,
      position: { x: (index + 1) * STEP_X, y: 200 },
      config: rcsNodeConfigFromAction(node),
    });
    edges.push({ id: `e_${prevId}_${node.id}`, source: prevId, target: node.id, condition: "always" });
    prevId = node.id;

    if (RCS_BRANCHING_TYPES.has(node.type)) {
      const trueId = node.config?.true_next as string | undefined;
      const falseId = node.config?.false_next as string | undefined;
      const branchCondition = node.type === "Branch" ? "postback" : "postback";
      if (trueId) {
        const trueNode = actionNodes.find((n) => n.id === trueId);
        if (trueNode) {
          nodes.push({
            id: trueNode.id,
            type: RCS_ACTION_TYPE_TO_NODE_TYPE[trueNode.type] as RcsFlowNode["type"],
            label: trueNode.type,
            position: { x: (index + 1) * STEP_X + STEP_X, y: 80 },
            config: rcsNodeConfigFromAction(trueNode),
          });
          edges.push({
            id: `e_${node.id}_${trueId}`,
            source: node.id,
            target: trueId,
            condition: branchCondition,
            value: node.type === "Branch" ? node.config?.equals : undefined,
          });
        }
      }
      if (falseId) {
        const falseNode = actionNodes.find((n) => n.id === falseId);
        if (falseNode) {
          nodes.push({
            id: falseNode.id,
            type: RCS_ACTION_TYPE_TO_NODE_TYPE[falseNode.type] as RcsFlowNode["type"],
            label: falseNode.type,
            position: { x: (index + 1) * STEP_X + STEP_X, y: 320 },
            config: rcsNodeConfigFromAction(falseNode),
          });
          edges.push({ id: `e_${node.id}_${falseId}`, source: node.id, target: falseId, condition: "default" });
        }
      }
    }
  });

  return { startNodeId: triggerId, nodes, edges, meta: { name, ...meta } };
}

export function rcsFlowToActionNodes(flow: RcsFlowDefinition): { triggerNode: TriggerNode; actionNodes: ActionNode[] } {
  const nodeMap = new Map(flow.nodes.map((n) => [n.id, n]));
  const outgoing = new Map<string, RcsFlowEdge[]>();
  flow.edges.forEach((e) => {
    outgoing.set(e.source, [...(outgoing.get(e.source) || []), e]);
  });

  const triggerRcsNode = flow.nodes.find((n) => n.id === flow.startNodeId) ?? flow.nodes.find((n) => n.type === "trigger");
  const rawKeywords: string[] = Array.isArray(triggerRcsNode?.config?.keywords) ? triggerRcsNode!.config.keywords : [];
  const triggerNode: TriggerNode = {
    type: "RCS Trigger Node",
    keywords: rawKeywords.length
      ? rawKeywords.map((text, i) => ({ id: makeKwId(i), text, match_similar: !!triggerRcsNode?.config?.match_similar }))
      : [
          { id: "kw_1", text: "hi", match_similar: false },
          { id: "kw_2", text: "hello", match_similar: false },
        ],
    restart_journey: false,
  };
  const actionNodes: ActionNode[] = [];
  const visited = new Set<string>();

  // Best-effort walk: follows the linear chain from the trigger, and for any
  // branching node takes its first two outgoing edges as true_next/false_next
  // (each branch target is appended depth-first, same shape hydrateJourneyToCanvas
  // produces for Condition nodes above).
  const walk = (nodeId: string) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    const node = nodeMap.get(nodeId);
    if (!node || node.type === "trigger") return;

    const actionType = NODE_TYPE_TO_RCS_ACTION_TYPE[node.type];
    if (!actionType) return;

    const edgesOut = outgoing.get(nodeId) || [];
    const config = rcsActionConfigFromNode(node);

    if (RCS_BRANCHING_TYPES.has(actionType)) {
      const trueEdge = edgesOut.find((e) => e.condition === "postback") || edgesOut[0];
      const falseEdge = edgesOut.find((e) => e.condition === "default") || edgesOut[1];
      config.true_next = trueEdge?.target ?? "";
      config.false_next = falseEdge?.target ?? "";
      actionNodes.push({ id: node.id, type: actionType, config, x: node.position?.x ?? 200, y: node.position?.y ?? 200 });
      if (trueEdge) walk(trueEdge.target);
      if (falseEdge) walk(falseEdge.target);
      return;
    }

    actionNodes.push({ id: node.id, type: actionType, config, x: node.position?.x ?? 200, y: node.position?.y ?? 200 });
    if (edgesOut[0]) walk(edgesOut[0].target);
  };

  const startEdges = outgoing.get(flow.startNodeId) || [];
  startEdges.forEach((e) => walk(e.target));

  return { triggerNode, actionNodes };
}